/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import MandateList from './MandateList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { deleteMandate, findManyMandates } from '../../lib/rest/mandateAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import Filters from '../../../../client/js/components/Filters'
import MandateStatusFilter from '../filters/MandateStatusFilter'
import MandateTypeFilter from '../filters/MandateTypeFilter'
import MandatePercentageFilter from '../filters/MandatePercentageFilter'
import WithEstateFilter from '../filters/WithEstateFilter'
import { getMandateCreateLink } from '../../lib/links/mandateLinks'
import CustomerInput from '../inputs/CustomerInput'
import Filter from '../Filter'
import { handleValueChange } from '../../../../client/js/lib/form'
import { Button } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'

export type MandateListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  percentage?: string,
  status?: string[],
  type?: string[],
  withEstate?: string,
  customerId?: string,
  estateId?: string,
}

export type MandateListCardProps = {
  createLink?: string;
  onQueryChange (query: SetStateAction<MandateListQuery>): void;
  query: MandateListQuery;
}

const initialQuery: MandateListQuery = {
  limit: '10',
  page: '1'
}

function MandateListCard (props: MandateListCardProps) {
  const {
    createLink = getMandateCreateLink(),
    query,
    onQueryChange
  } = props

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['mandates', queryParams],
    queryFn: () => findManyMandates(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })

  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.mandates : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteMandate(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <MandateStatusFilter
          onChange={setFilter}
          value={queryParams.status}
        />
        <MandateTypeFilter
          onChange={setFilter}
          value={queryParams.type}
        />
        <WithEstateFilter
          onChange={setFilter}
          value={queryParams.withEstate}
        />
        <MandatePercentageFilter
          onChange={setFilter}
          value={queryParams.percentage}
        />
        <Filter>
          <CustomerInput
            onChange={handleValueChange('customerId', setFilter)}
            value={queryParams.customerId}
          />
        </Filter>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:mandate']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:mandate']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_mandate"
                defaultMessage="Créer un mandat"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
      </Toolbar>}
    >
      <LoadingHelper
        query={listQuery}
        render={() => (
          <MandateList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </ListCard>
  )
}

export default MandateListCard
